import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  PiBuildings,
  PiClock,
  PiDownloadSimple,
  PiEnvelope,
  PiPhone,
  PiUser,
} from 'react-icons/pi'
import {
  EditPinsModal,
  EntityPreviewCard,
  EntityPreviewRow,
  Link,
  PinnedWidget,
  RecentlyVisitedWidget,
  UpcomingWidget,
  type PinnedItem,
  type RecentlyVisitedItem,
  type UpcomingReminder,
  type EntityType,
} from '@firstbite/ui'
import { useAuth } from 'context/authentication/useAuth'
import {
  PAGE_KEYS,
  type PinListItem,
  type RecentListItem,
  useDeleteHomepagePin,
  useListHomepagePins,
  useListHomepageRecents,
  useReorderHomepagePins,
} from 'services/homepage'
import {
  type ReminderList,
  useDeleteReminder,
  useListReminders,
} from 'services/reminders'
import { toast } from 'react-toastify'
import ReminderModal from '../Reminders/ReminderModal'
import confirm from '../dialogConfirm'

/**
 * QuickAccessCardFbUi — the fb-ui (`@firstbite/ui`) rendering of the homepage
 * Quick Access basics (Pinned, Recently visited, Upcoming). This is the first
 * in-app adoption of the heavy `@firstbite/ui` widgets on a desktop surface; it
 * replaced the legacy hand-rolled `QuickAccessCard` outright (rendered under
 * `enableQuickAccess` in `home/index.tsx`).
 *
 * Layout mirrors the fb-ui `Homepage` block's "basics" row: Pinned full-width,
 * then Recently Visited | Upcoming two-up (stacks on narrow). Each widget owns
 * its own card, header, and empty state.
 *
 * When there are no upcoming reminders — including the all-empty case — the
 * Upcoming widget is hidden and Pinned + Recently Visited render two-up instead,
 * empty states and all. This keeps the row compact while still showing the
 * homepage's potential (rather than a dead Upcoming card or a collapsed stub).
 * A future customizable homepage may let users choose which widgets show.
 */

// Page pins/recents carry their specific page in `page_key`. Map each registry
// page (keyed off the `PAGE_KEYS` mirror of the server PAGE_REGISTRY) to a
// distinct fb-ui glyph so pages don't all collapse to the generic Browser icon.
// `document`/`product`/`reminder` reuse existing record glyphs; `settings`/
// `brands`/`activity` are page-glyphs added to fb-ui. (Settings mirrors its
// left-nav cog; Products/Brands/Activity live under the single Workspace nav
// item, so their glyphs are bespoke rather than nav-matched.) Unmapped keys →
// `page`.
const PAGE_KEY_ENTITY: Record<string, EntityType> = {
  [PAGE_KEYS.settings]: 'settings',
  [PAGE_KEYS.documents]: 'document',
  [PAGE_KEYS.products]: 'product',
  [PAGE_KEYS.brands]: 'brands',
  [PAGE_KEYS.activity]: 'activity',
  [PAGE_KEYS.reminders]: 'reminder',
}

// The API `target_type` is a bare string on the wire. Narrow it to the fb-ui
// `EntityType` union (a superset of the client's pin/recent types). Page pins
// additionally resolve via `page_key` (above), so distinct pages don't all
// collapse to one generic glyph. Anything unrecognized falls back to `page` —
// the neutral glyph — so an unexpected type can never crash a tile/row.
export function entityTypeFromApi(
  targetType: string,
  pageKey?: string | null
): EntityType {
  if (pageKey && PAGE_KEY_ENTITY[pageKey]) return PAGE_KEY_ENTITY[pageKey]
  switch (targetType) {
    case 'company':
    case 'deal':
    case 'campaign':
    case 'contact':
    case 'note':
    case 'document':
    case 'page':
      return targetType
    default:
      return 'page'
  }
}

// Note pins arrive with rich-text markup in labels/bodies \u2014 HTML tags
// ("<p>New note</p>") and/or bare entities ("longer&nbsp;still"). Strip tags +
// decode entities so tiles/rows/previews show clean text. Plain labels (no `<`
// or `&`) skip parsing untouched; DOMParser leaves stray ampersands (e.g.
// "R&D") intact since they aren't valid entities.
export function plainText(value: string | null | undefined): string {
  const v = value ?? ''
  if (!v.includes('<') && !v.includes('&')) return v
  const doc = new DOMParser().parseFromString(v, 'text/html')
  return (doc.body.textContent ?? '').replace(/\u00a0/g, ' ').trim()
}

// Format a timestamp for a preview detail row. Note `created` arrives as a raw
// ISO string; document `uploaded_on` is already human ("Mar 19, 2026"). Parse
// what we can into a readable date, and pass through anything unparseable.
export function formatWhen(value: string | undefined): string {
  if (!value) return ''
  const d = new Date(value)
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
}

// Short relative-time label for a recently-visited row ("9m ago", "Yesterday",
// "May 28"). fb-ui renders this string verbatim as the row's `visitedLabel`.
function relativeWhen(iso: string): string {
  const then = new Date(iso)
  const diffMin = Math.round((Date.now() - then.getTime()) / 60_000)
  if (diffMin < 1) return 'Just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.round(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.round(diffHr / 24)
  if (diffDay === 1) return 'Yesterday'
  if (diffDay < 7) return `${diffDay}d ago`
  return then.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

// Recurring reminders carry a humanized recurrence label; one-off reminders omit
// it (the widget renders nothing for the empty case).
const RECURRENCE_LABEL: Partial<
  Record<NonNullable<ReminderList['reminder_type']>, string>
> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annually: 'Annually',
  custom: 'Custom',
}

// Record target_types with no dedicated page — a pin of one shows its detail in
// a popover instead of navigating. Keyed on the RAW API `target_type` (a record
// kind), NOT the resolved glyph type: a *page* pin such as the Documents page
// (`target_type: 'page'`, `page_key: 'workspace.documents'`) resolves to the
// `document` glyph but is still a page — it must navigate via `href`, not open an
// (empty) record preview. Gating on the glyph type instead conflates the two.
const PREVIEW_TARGET_TYPES = new Set(['contact', 'note', 'document'])

// The responsive two-up row shared by both layout branches (Pinned+Recently, or
// Recently+Upcoming). `gap-[1rem]` — not `gap-4` — is deliberate: Bootstrap's
// global `.gap-4 { gap:1.5rem !important }` in the app shell would otherwise
// shadow Tailwind's 1rem. See reference: fb-ui ↔ Bootstrap class collision.
const TWO_COLUMN_ROW = 'grid grid-cols-1 gap-[1rem] lg:grid-cols-2'

// First Bite–specific copy for the Pinned widget's help popover.
const PINNED_HELP = (
  <>
    Pin the records and pages you reach for most — companies, deals, campaigns,
    contacts, notes, documents, and any workspace or settings page — for
    one-click access here. Use a record&apos;s <strong>Pin</strong> action to
    add it.
  </>
)

// The fb-ui `EntityPreviewCard` shown in a pinned contact/note/document tile's
// popover, built from the API `card` detail — the fb-ui equivalent of the
// legacy popup cards. `onClose` wires the card's × to dismiss the popover.
function PinPreviewCard({
  item,
  onClose,
}: {
  item: PinListItem
  onClose: () => void
}) {
  const card = item.card ?? {}
  const name = plainText(item.label)
  switch (entityTypeFromApi(item.target_type)) {
    case 'contact':
      return (
        <EntityPreviewCard
          type="contact"
          hideGlyph
          name={name}
          // Just the title in the subtitle — company gets its own row below.
          subtitle={card.role || undefined}
          onClose={onClose}
        >
          {card.company && (
            // Company as its own row (not gray subtitle text). TODO: link to the
            // company page once the pin `card` payload includes a company href —
            // it currently ships the name only.
            <EntityPreviewRow icon={<PiBuildings />}>
              {card.company}
            </EntityPreviewRow>
          )}
          {card.email && (
            <EntityPreviewRow icon={<PiEnvelope />}>
              <Link variant="primary" size="sm" href={`mailto:${card.email}`}>
                {card.email}
              </Link>
            </EntityPreviewRow>
          )}
          {card.phone && (
            <EntityPreviewRow icon={<PiPhone />}>
              <Link variant="primary" size="sm" href={`tel:${card.phone}`}>
                {card.phone}
              </Link>
            </EntityPreviewRow>
          )}
        </EntityPreviewCard>
      )
    case 'note':
      return (
        <EntityPreviewCard
          type="note"
          hideGlyph
          name={name}
          subtitle={card.related_to || undefined}
          onClose={onClose}
        >
          {card.content && (
            // Show ~5 lines, then scroll for longer notes.
            <p className="fbui-scroll max-h-[6.25rem] overflow-y-auto whitespace-pre-line text-sm text-fbui-gray-700">
              {plainText(card.content)}
            </p>
          )}
          {card.author && (
            <EntityPreviewRow icon={<PiUser />}>{card.author}</EntityPreviewRow>
          )}
          {card.created && (
            <EntityPreviewRow icon={<PiClock />}>
              {formatWhen(card.created)}
            </EntityPreviewRow>
          )}
        </EntityPreviewCard>
      )
    case 'document':
      return (
        <EntityPreviewCard
          type="document"
          hideGlyph
          name={name}
          subtitle={
            [card.file_extension, card.size].filter(Boolean).join(' · ') ||
            'Document'
          }
          // Filenames are long — let the title wrap instead of truncating (the
          // "…" the header was showing).
          wrapTitle
          onClose={onClose}
          actions={
            card.download_url ? (
              <Link
                variant="primary"
                size="sm"
                leftIcon={<PiDownloadSimple />}
                href={card.download_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                Download
              </Link>
            ) : undefined
          }
        >
          {card.uploaded_by && (
            <EntityPreviewRow icon={<PiUser />}>
              Uploaded by {card.uploaded_by}
            </EntityPreviewRow>
          )}
          {card.uploaded_on && (
            <EntityPreviewRow icon={<PiClock />}>
              {formatWhen(card.uploaded_on)}
            </EntityPreviewRow>
          )}
        </EntityPreviewCard>
      )
    default:
      return null
  }
}

// API pin → fb-ui `EntityRef`. Record/page pins carry an `href` (link tile);
// contact/note/document pins carry a `preview` popover instead (it takes
// precedence over `href` in the tile).
export function pinnedItemFromApi(item: PinListItem): PinnedItem {
  const type = entityTypeFromApi(item.target_type, item.page_key)
  const isPage = item.target_type === 'page'
  return {
    id: String(item.id),
    type,
    // Page pins keep their bespoke glyph (cog, file, …) but read "Page" rather
    // than the borrowed record label ("Document") — they're navigation targets,
    // not records, and grouping them under one caption reads cleaner.
    typeLabel: isPage ? 'Page' : undefined,
    name: plainText(item.label),
    href: item.href ?? undefined,
    preview: PREVIEW_TARGET_TYPES.has(item.target_type)
      ? (close: () => void) => <PinPreviewCard item={item} onClose={close} />
      : undefined,
  }
}

// API recent → fb-ui `RecentlyVisitedItem`, or `null` to drop it. Visit tracking
// only records navigable things — records with a page (company/deal/campaign)
// and registry pages — so every recent carries an `href` today. Defensively drop
// any that don't: a hrefless row can't navigate and a click would silently
// no-op. (The legacy card filtered by record *type*; we guard on `href` instead,
// since registry pages now legitimately appear here and resolve to varied types
// like `document`/`product` — a type filter would wrongly drop those.)
export function recentItemFromApi(
  item: RecentListItem
): RecentlyVisitedItem | null {
  if (!item.href) return null
  return {
    id: String(item.id),
    type: entityTypeFromApi(item.target_type, item.page_key),
    name: plainText(item.label),
    visitedLabel: relativeWhen(item.visited_at),
    href: item.href,
  }
}

// API reminder → fb-ui `UpcomingReminder`. fb-ui does its own Today/Tomorrow +
// time formatting, so we pass the raw due `Date`. Reminders with no scheduled
// time are dropped (the query filters to future reminders, so this is defensive).
function upcomingReminderFromApi(
  reminder: ReminderList
): UpcomingReminder | null {
  const iso = reminder.next_run_at ?? reminder.run_at
  if (!iso) return null
  return {
    id: String(reminder.id),
    title: reminder.title,
    dueAt: new Date(iso),
    recurrence: reminder.reminder_type
      ? RECURRENCE_LABEL[reminder.reminder_type]
      : undefined,
  }
}

export function QuickAccessCardFbUi() {
  const navigate = useNavigate()
  const { user } = useAuth()
  // One modal drives both flows: "Schedule reminder" (create, no id) from the
  // widget menu, and opening an existing reminder (edit) from a row click.
  const [reminderModal, setReminderModal] = useState<{
    open: boolean
    reminderId?: number
  }>({ open: false })
  const [editPinsOpen, setEditPinsOpen] = useState(false)
  const deleteReminder = useDeleteReminder()
  const reorderPins = useReorderHomepagePins()
  const deletePin = useDeleteHomepagePin()

  // Commit the Edit-Pins draft: the modal returns the final ordered list (with
  // removed pins already dropped). Delete the pins that fell out, then persist
  // the new order of the survivors. Both mutations invalidate the pins query, so
  // the widget re-renders from the fresh server list.
  async function handlePinsChange(next: PinnedItem[]) {
    const removed = pins.filter((p) => !next.some((n) => n.id === p.id))
    try {
      await Promise.all(
        removed.map((p) => deletePin.mutateAsync({ id: String(p.id) }))
      )
      if (next.length > 0) {
        await reorderPins.mutateAsync({
          data: { pin_ids: next.map((p) => Number(p.id)) },
        })
      }
    } catch {
      toast.error('Failed to update pins. Please try again.')
    }
  }

  // Row hover "Delete" — confirm, then delete. The mutation invalidates the
  // reminders query, so the Upcoming widget refetches and the row drops out.
  async function handleRemoveReminder(reminderId: number, title: string) {
    const confirmed = await confirm(
      <>
        Delete the reminder <strong>{title}</strong>? This can&apos;t be undone.
      </>,
      'Delete reminder?',
      undefined,
      { confirmLabel: 'Delete', buttonVariant: 'danger' }
    )
    if (!confirmed) return
    try {
      await deleteReminder.mutateAsync({ id: reminderId })
    } catch {
      toast.error('Failed to delete reminder. Please try again.')
    }
  }

  // Stable at mount — recomputing inline produces a new queryKey every render,
  // which refetches forever.
  const scheduledAfter = useMemo(() => new Date().toISOString(), [])

  const { data: apiPins, isLoading: pinsLoading } = useListHomepagePins()
  const { data: apiRecents, isLoading: recentsLoading } =
    useListHomepageRecents()
  // Upcoming = this user's active reminders that haven't fired yet, soonest
  // first, capped small so the card stays compact.
  const { data: apiReminders, isLoading: remindersLoading } = useListReminders(
    {
      recipient: user?.id ? [user.id] : undefined,
      is_active: true,
      scheduled_after: scheduledAfter,
      ordering: 'next_run_at',
      page_size: 5,
    },
    { query: { enabled: Boolean(user?.id) } }
  )

  // First load — show the widgets' own loading skeletons in a representative
  // layout (Pinned full-width, then Recently Visited | Upcoming two-up) so the
  // basics don't pop in from blank. We can't yet know whether Upcoming will be
  // empty (that decides the final layout), so we render the full three-widget
  // shape; if it turns out empty, the loaded view collapses to the two-up
  // Pinned | Recently layout below.
  if (pinsLoading || recentsLoading || remindersLoading) {
    return (
      <div className="space-y-4">
        <PinnedWidget items={[]} loading className="max-w-none" />
        <div className={TWO_COLUMN_ROW}>
          <RecentlyVisitedWidget items={[]} loading className="max-w-none" />
          <UpcomingWidget reminders={[]} loading className="max-w-none" />
        </div>
      </div>
    )
  }

  const pins: PinnedItem[] = (apiPins ?? []).map(pinnedItemFromApi)
  const recents: RecentlyVisitedItem[] = (apiRecents ?? [])
    .map(recentItemFromApi)
    .filter((r): r is RecentlyVisitedItem => r !== null)
  const upcoming: UpcomingReminder[] = (apiReminders?.results ?? [])
    .map(upcomingReminderFromApi)
    .filter((r): r is UpcomingReminder => r !== null)

  const upcomingEmpty = upcoming.length === 0

  // `fillHeight` is set only in the two-up branch, where Pinned is half-width and
  // the grid stretches it to match the taller Recently Visited column — the tiles
  // grow to fill rather than leaving dead space below a single row. Full-width
  // (below), the card is only as tall as its content, so no fill is needed.
  const renderPinned = (fillHeight?: boolean) => (
    <PinnedWidget
      items={pins}
      help={PINNED_HELP}
      fillHeight={fillHeight}
      // Wiring `onEdit` adds the ⋯ menu's "Edit pins" item and opens the
      // controlled EditPinsModal below.
      onEdit={() => setEditPinsOpen(true)}
      // Record/page pins navigate via their `href` link; contact/note/document
      // pins open their `preview` popover. `onItemClick` is a defensive fallback
      // for any tile with neither.
      onItemClick={(item) => item.href && navigate(item.href)}
      // The widgets cap themselves at `max-w-3xl` in isolation (Storybook); on
      // the homepage they should fill the content column / grid cell, so drop
      // the cap. (`cn` merges this last, overriding the `max-w-3xl`.)
      className="max-w-none"
    />
  )

  const recentlyVisitedWidget = (
    <RecentlyVisitedWidget
      items={recents}
      onItemClick={(item) => item.href && navigate(item.href)}
      className="max-w-none"
    />
  )

  return (
    <>
      {upcomingEmpty ? (
        // No upcoming reminders: hide the Upcoming widget and show Pinned +
        // Recently Visited side-by-side so the row doesn't carry an empty card.
        <div className={TWO_COLUMN_ROW}>
          {renderPinned(true)}
          {recentlyVisitedWidget}
        </div>
      ) : (
        // Pinned full-width, then Recently Visited | Upcoming two-up.
        <div className="space-y-4">
          {renderPinned()}
          <div className={TWO_COLUMN_ROW}>
            {recentlyVisitedWidget}
            <UpcomingWidget
              reminders={upcoming}
              // Row hover reveals Edit + Delete icons (wiring these turns on the
              // hover-action affordance). Edit opens the reminder in the modal;
              // Delete confirms then removes it.
              onEdit={(reminder) =>
                setReminderModal({
                  open: true,
                  reminderId: Number(reminder.id),
                })
              }
              onRemove={(reminder) =>
                handleRemoveReminder(Number(reminder.id), reminder.title)
              }
              onSchedule={() => setReminderModal({ open: true })}
              onViewAll={() => navigate('/reminders')}
              className="max-w-none"
            />
          </div>
        </div>
      )}

      <ReminderModal
        // Remount between create/edit so the form never carries stale values.
        key={reminderModal.reminderId ?? 'new'}
        open={reminderModal.open}
        reminderId={reminderModal.reminderId}
        onClose={() => setReminderModal({ open: false })}
      />

      <EditPinsModal
        // Controlled from the Pinned ⋯ menu; the trigger is unused (a hidden
        // placeholder) since the menu item drives the open state.
        trigger={<span className="hidden" />}
        isOpen={editPinsOpen}
        onOpenChange={setEditPinsOpen}
        items={pins}
        onApply={handlePinsChange}
      />
    </>
  )
}
